import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spin, Result, Button, message } from "antd";
import {
  BookingWizardProvider,
  BookingState,
  useBookingWizard,
} from "../contexts/BookingWizardContext";
import { useCompany } from "../hooks/useCompany";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { supabase } from "../lib/supabase";
import { BookingWizardV2 } from "../components/BookingWizardV2/BookingWizardV2";
import {
  buildInitialV2State,
  isV2Booking,
  markV2Booking,
} from "../components/BookingWizardV2/utils/ensureDraftBooking";
import { fetchCachedCommercialInvoiceAnalysis } from "../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { getPrimaryCommercialInvoicePath } from "../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { getCachedAnalysisForPath } from "../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisCache";

export const BookingWizardV2Page = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const {
    details: companyDetails,
    loading: companyLoading,
    error,
  } = useCompanyDetails(selectedCompany);
  const [initialData, setInitialData] = useState<BookingState | undefined>();
  const [initialV2Step, setInitialV2Step] = useState(0);
  const [loading, setLoading] = useState(true);

  const isCompanyApproved =
    companyDetails &&
    companyDetails.status &&
    companyDetails.status !== "unapproved" &&
    companyDetails.status !== "under_review";

  const hasCompanyError = error && !companyDetails;

  const fromProductPage = location.state?.fromProductPage;
  const returnToStep = location.state?.returnToStep;
  const forceStep = location.state?.forceStep;

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      if (companyLoading) return;
      if (hasCompanyError) {
        setLoading(false);
        return;
      }
      if (companyDetails && !isCompanyApproved) {
        setLoading(false);
        return;
      }

      if (fromProductPage && typeof returnToStep === "number") {
        setInitialV2Step(1);
      } else if (fromProductPage && typeof forceStep === "number") {
        setInitialV2Step(1);
      }

      if (id) {
        try {
          const { data, error: fetchError } = await supabase
            .from("bookings")
            .select(
              `
              *,
              transport_type:booking_transport_types(id, name, abbreviation),
              booking_type:bookings_type(id, name),
              booking_incoterm:bookings_incoterm(id, name),
              supplier:booking_supplier(id, name, email, phone, wechat, country, province, city, address),
              products:booking_product(id, name, hs_code, ean, gross_weight, cbm, value, pieces, cartons),
              delivery_details:booking_delivery(id, country, city, postal_code, street_name, house_number, instructions)
            `
            )
            .eq("id", id)
            .single();

          if (fetchError) {
            message.error("Failed to load booking");
            return;
          }

          if (
            data.status === "submitted" ||
            data.status === "approved" ||
            (data.status !== "concept" && data.status !== "rejected")
          ) {
            navigate(`/bookings/${id}/view`, { replace: true });
            return;
          }

          markV2Booking(data.id);
          const transformed = buildInitialV2State(data, 1);

          if (fromProductPage && location.state?.previousProducts) {
            transformed.products = location.state.previousProducts;
            transformed.currentStep = 1;
          }

          const ciPath = getPrimaryCommercialInvoicePath(
            data.commercial_invoice_file
          );
          let startStep =
            fromProductPage && (returnToStep != null || forceStep != null)
              ? 1
              : 0;
          if (ciPath) {
            const { data: cachedRow } = await fetchCachedCommercialInvoiceAnalysis(
              data.id
            );
            if (getCachedAnalysisForPath(cachedRow, ciPath)) {
              startStep = 1;
            }
          } else if (
            transformed.transport_type &&
            transformed.products &&
            transformed.products.length > 0
          ) {
            startStep = 1;
          }

          setInitialV2Step(startStep);
          setInitialData(transformed);
        } catch (err) {
          console.error(err);
          message.error("An error occurred while loading the booking");
        }
      }

      setLoading(false);
    };

    load();
  }, [
    id,
    companyDetails,
    isCompanyApproved,
    companyLoading,
    hasCompanyError,
    navigate,
    fromProductPage,
    returnToStep,
    forceStep,
    location.state,
  ]);

  if (companyLoading || loading) {
    return <Spin size="large" />;
  }

  if (hasCompanyError) {
    return (
      <Result
        status="error"
        title="Company Not Found"
        subTitle="The selected company could not be found."
        extra={[
          <Button key="company" type="primary" onClick={() => navigate("/company-info")}>
            Go to Company Info
          </Button>,
          <Button key="back" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </Button>,
        ]}
      />
    );
  }

  if (!isCompanyApproved) {
    return (
      <Result
        status="warning"
        title="Company Not Approved"
        subTitle="Complete your company information to create bookings."
        extra={[
          <Button key="company" type="primary" onClick={() => navigate("/company-info")}>
            Go to Company Info
          </Button>,
          <Button key="back" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </Button>,
        ]}
      />
    );
  }

  return (
    <BookingWizardProvider initialData={initialData}>
      <V2StepBootstrap initialStep={initialV2Step} />
      <BookingWizardV2
        initialStep={initialV2Step}
        onClose={() => navigate("/bookings")}
      />
    </BookingWizardProvider>
  );
};

const V2StepBootstrap = ({ initialStep }: { initialStep: number }) => {
  const { setCurrentStep, state } = useBookingWizard();
  const bootstrappedRef = useRef<number | null>(null);

  useEffect(() => {
    const target = initialStep >= 2 ? 4 : initialStep >= 1 ? 1 : 0;
    if (bootstrappedRef.current === initialStep && state.currentStep === target) {
      return;
    }
    if (state.currentStep !== target) {
      setCurrentStep(target);
    }
    bootstrappedRef.current = initialStep;
  }, [initialStep, setCurrentStep, state.currentStep]);

  return null;
};

/** Used by BookingEditPage to optionally render V2 for concept bookings started via AI wizard. */
export const shouldUseV2Wizard = (bookingId?: number): boolean => {
  if (!bookingId) return false;
  return isV2Booking(bookingId);
};
